import {
  DocumentStopEntry,
  LoadConfirmationDocumentData,
  LoadConfirmationPageSlice,
  LoadConfirmationStop,
} from '../types'
import {
  displayValue,
  formatDocumentDate,
  formatDocumentTime,
  formatRateValue,
  splitAddressLines,
} from '../utils/format-document'
import CompanyLogo from 'components/company-logo/company-logo.component'

function StopBlock({
  entry,
}: {
  entry: DocumentStopEntry
}) {
  const { kind, stop, stopNumber } = entry
  const isShipper = kind === 'shipper'
  const [addr1, addr2] = splitAddressLines(stop.address)
  const scheduleDate = formatDocumentDate(stop.startTime || stop.endTime)
  const scheduleTime = formatDocumentTime(stop.startTime || stop.endTime)

  return (
    <div className="lc-stop-block">
      <div className="lc-section-title lc-blue">
        {isShipper ? 'Shipper' : 'Consignee'}
      </div>
      <hr className="lc-rule" />
      <div className="lc-stop-grid">
        <div className="lc-stop-col-main">
          <div className="lc-stop-head">
            <span className="lc-stop-number">{stopNumber}</span>
            <div className="lc-stop-details">
              <div className="lc-stop-name">{displayValue(stop.name, '—')}</div>
              <div>{displayValue(addr1)}</div>
              <div>{displayValue(addr2)}</div>
            </div>
          </div>
        </div>
        <div className="lc-stop-col-meta">
          {isShipper ? (
            <>
              <div className="lc-meta-row">
                <span className="lc-meta-label">FTL or LTL: </span>
                {displayValue(stop.loadType, 'FTL')}
              </div>
              <div className="lc-meta-row">
                <span className="lc-meta-label">Load Count: </span>
                {displayValue(stop.loadCount)}
              </div>
              <div className="lc-meta-row">
                <span className="lc-meta-label">Temp: </span>
                {displayValue(stop.temperatureValue)}
              </div>
              <div className="lc-meta-row">
                <span className="lc-meta-label">Hazmat: </span>
                {displayValue(stop.hazmat)}
              </div>
              <div className="lc-meta-row">
                <span className="lc-meta-label">Weight: </span>
                {displayValue(stop.weightValue)}
              </div>
              <div className="lc-meta-row">
                <span className="lc-meta-label">Pickup#: </span>
                <span className="lc-pickup-num">{displayValue(stop.pickupNumber)}</span>
              </div>
            </>
          ) : null}
        </div>
        <div className="lc-stop-col-schedule">
          <div className="lc-schedule-line">
            <span className="lc-meta-label">
              {isShipper ? 'Pickup Date: ' : 'Delivery Date: '}
            </span>
            <span className="lc-date">{scheduleDate || '—'}</span>
          </div>
          <div className="lc-schedule-line">
            <span className="lc-meta-label">
              {isShipper ? 'Pickup Time: ' : 'Delivery Time: '}
            </span>
            <span className="lc-time">{scheduleTime || '—'}</span>
          </div>
        </div>
      </div>
      <div className="lc-stop-notes-full">
        <div className="lc-notes-label">
          {isShipper ? 'Shipper Notes' : 'Consignee Notes'}
        </div>
        <div>{displayValue(stop.notes)}</div>
      </div>
    </div>
  )
}

function FullHeader({
  data,
  logoUrl,
}: {
  data: LoadConfirmationDocumentData
  logoUrl?: string
}) {
  const [addr1, addr2] = splitAddressLines(data.myCompany.address)
  const { myCompany: co } = data

  return (
    <header className="lc-header-full">
      <div>
        {logoUrl ? (
          <div className="lc-logo-wrap">
            <CompanyLogo src={logoUrl} maxHeight={92} maxWidth={150} alt="Company logo" />
          </div>
        ) : null}
        <div className="lc-company-name">{displayValue(co.name, 'User Company Name')}</div>
        <div>{displayValue(addr1, 'User Company Address 1')}</div>
        <div>{displayValue(addr2, 'User Company Address 2')}</div>
      </div>
      <div className="lc-header-center">
        <div><span className="lc-label">Issued by: </span>{displayValue(co.issuedBy)}</div>
        <div><span className="lc-label">Email: </span>{displayValue(co.email)}</div>
        <div><span className="lc-label">Phone: </span>{displayValue(co.phone)}</div>
        <div><span className="lc-label">Cell: </span>{displayValue(co.cell)}</div>
        <div>
          <span className="lc-label">After Hours Phone: </span>
          {displayValue(co.afterHoursPhone)}
        </div>
        <div style={{ marginTop: 6 }}>
          <div>
            <span className="lc-label">Invoice &amp; POD Name: </span>
            {displayValue(co.invoicePodName)}
          </div>
          <div>
            <span className="lc-label">Invoice &amp; POD Email: </span>
            {displayValue(co.invoicePodEmail)}
          </div>
          <div>
            <span className="lc-label">Invoice &amp; POD Phone: </span>
            {displayValue(co.invoicePodPhone)}
          </div>
        </div>
      </div>
      <div className="lc-header-right">
        <div className="lc-doc-title">Load Confirmation</div>
        <div className="lc-load-number">{displayValue(data.loadNumber, 'LCLC-000000')}</div>
      </div>
    </header>
  )
}

function CarrierSection({ data }: { data: LoadConfirmationDocumentData }) {
  const [addr1, addr2] = splitAddressLines(data.carrier.address)
  const { carrier: c, rate: r } = data

  return (
    <>
      <div className="lc-section-title lc-blue">Carrier</div>
      <hr className="lc-rule" />
      <div className="lc-carrier-grid">
        <div>
          <div className="lc-bold">{displayValue(c.name)}</div>
          <div>{displayValue(addr1)}</div>
          <div>{displayValue(addr2)}</div>
        </div>
        <div>
          <div><span className="lc-field-label">Dispatcher Name: </span>{displayValue(c.dispatchName)}</div>
          <div><span className="lc-field-label">Dispatcher email: </span>{displayValue(c.dispatchEmail)}</div>
          <div><span className="lc-field-label">Dispatcher Phone: </span>{displayValue(c.dispatchPhone)}</div>
          <div><span className="lc-field-label">Dispatcher Cell: </span>{displayValue(c.dispatchCell)}</div>
          <div>
            <span className="lc-field-label">Dispatcher After Hours: </span>
            {displayValue(c.dispatchAfterHours)}
          </div>
        </div>
        <div>
          <div>
            <span className="lc-field-label">Base Rate: </span>
            {formatRateValue(r.baseRate, r.currency) || '—'}
          </div>
          <div>
            <span className="lc-field-label">Detention Charges: </span>
            {formatRateValue(r.detentionCharges, r.currency) || '—'}
          </div>
          <div>
            <span className="lc-field-label">Layover Charges: </span>
            {formatRateValue(r.layoverCharges, r.currency) || '—'}
          </div>
          <div>
            <span className="lc-field-label">Fuel Tax: </span>
            {formatRateValue(r.fuelSurcharge, r.currency) || '—'}
          </div>
          <div>
            <span className="lc-field-label">Other Charge: </span>
            {formatRateValue(r.otherCharges, r.currency) || '—'}
          </div>
        </div>
      </div>
    </>
  )
}

function PageFooter({
  loadNumber,
  pageNumber,
  totalPages,
}: {
  loadNumber: string
  pageNumber: number
  totalPages: number
}) {
  return (
    <footer className="lc-page-footer">
      <span>Load Confirmation {displayValue(loadNumber)}</span>
      <span>
        Page {pageNumber} of {totalPages}
      </span>
    </footer>
  )
}

function DocumentPage({
  data,
  slice,
  logoUrl,
}: {
  data: LoadConfirmationDocumentData
  slice: LoadConfirmationPageSlice
  logoUrl?: string
}) {
  return (
    <div className="lc-a4-page" data-page={slice.pageNumber}>
      <div className="lc-page-body">
        {slice.headerVariant === 'full' ? (
          <FullHeader data={data} logoUrl={logoUrl} />
        ) : (
          <div className="lc-header-mini">
            {displayValue(data.myCompany.name, 'User Company Name')}
          </div>
        )}

        {slice.showCarrier && <CarrierSection data={data} />}

        {slice.stops.map((entry) => (
          <StopBlock
            key={`${entry.kind}-${entry.stopNumber}`}
            entry={entry}
          />
        ))}

        {slice.notesText != null && slice.notesText !== '' && (
          <div
            className={
              slice.notesContinued
                ? 'lc-notes-section lc-notes-section--continued'
                : 'lc-notes-section'
            }
          >
            {!slice.notesContinued && (
              <>
                <div className="lc-section-title lc-blue">Note</div>
                <hr className="lc-rule" />
              </>
            )}
            <div className="lc-notes-body">{slice.notesText}</div>
          </div>
        )}
      </div>

      <PageFooter
        loadNumber={data.loadNumber}
        pageNumber={slice.pageNumber}
        totalPages={slice.totalPages}
      />
    </div>
  )
}

export type LoadConfirmationDocumentProps = {
  data: LoadConfirmationDocumentData
  pages: LoadConfirmationPageSlice[]
  logoUrl?: string
  /** When true, renders for PDF capture (no scale transform) */
  forExport?: boolean
}

export function LoadConfirmationDocument({
  data,
  pages,
  logoUrl,
}: LoadConfirmationDocumentProps) {
  return (
    <div className="lc-document-root">
      {pages.map((slice) => (
        <DocumentPage
          key={slice.pageNumber}
          data={data}
          slice={slice}
          logoUrl={logoUrl}
        />
      ))}
    </div>
  )
}

export type { LoadConfirmationStop }
